/**
 * AddButton — the "Add" control that becomes a − quantity + stepper.
 *
 * This is the signature quick-commerce interaction (Blinkit/Zepto): tap "Add"
 * and it morphs in place into a stepper. Reading the cart from context means
 * the stepper stays correct no matter WHERE the same product appears — add it
 * on the home page and the product-detail page shows the same count.
 *
 * Brand note: outlined gold when empty, solid golden-yellow stepper when active
 * with dark ink text — the reference app's green, recoloured to Rocket07 gold.
 */

import { Plus, Minus } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { cn } from '@/lib/cn';

export function AddButton({ product, size = 'md', className }) {
  const { getQuantity, addItem, decrement } = useCart();
  const quantity = getQuantity(product.id);

  const heights = size === 'lg' ? 'h-11 text-base' : 'h-9 text-sm';

  if (quantity === 0) {
    return (
      <button
        type="button"
        onClick={() => addItem(product)}
        className={cn(
          'flex w-full items-center justify-center rounded-lg border border-primary-400 bg-primary-50/60 font-bold uppercase tracking-wide text-primary-700',
          'transition-colors hover:bg-primary-500 hover:text-ink active:scale-[0.98]',
          heights,
          className,
        )}
        aria-label={`Add ${product.name} to cart`}
      >
        Add
      </button>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full items-center justify-between rounded-lg bg-primary-500 font-bold text-ink',
        heights,
        className,
      )}
    >
      <button
        type="button"
        onClick={() => decrement(product.id)}
        className="grid h-full w-10 place-items-center rounded-l-lg transition-colors hover:bg-primary-600 active:scale-95"
        aria-label="Decrease quantity"
      >
        <Minus size={16} />
      </button>
      <span className="tabular-nums" aria-live="polite">
        {quantity}
      </span>
      <button
        type="button"
        onClick={() => addItem(product)}
        className="grid h-full w-10 place-items-center rounded-r-lg transition-colors hover:bg-primary-600 active:scale-95"
        aria-label="Increase quantity"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

export default AddButton;
